<template>
  <el-container class="screenHeight">
    <el-header class="level">
      <span class="level-left">
        <el-page-header @back="handleBack">
          <template #title>
            {{ $t('components.embossing-footer.backButton') }}
          </template>

          <template #content>
            {{ $t(`enum.order-progress.${activeOrderProgress}`) }}
          </template>
        </el-page-header>
      </span>
      <span class="level-right">
        <!--<el-button
          v-loading.fullscreen.lock="fullscreenLoading"
          link
          class="level-item"
          :disabled="!hasChanges"
          @click="saveUpdated"
        >
          <font-awesome-icon icon="save" />
        </el-button>-->
        <span class="level-item">
          {{ authentication.user?.firstname }}
          {{ authentication.user?.lastname }}
        </span>
      </span>
    </el-header>

    <el-container>
      <el-aside v-if="showTree" class="withFooterAndHeader" width="20rem">
        <el-scrollbar>
          <MeshTree
            v-model="meshTreeData"
            :add-url="`/order-upload?templateId=${templateId}&orderId=${orderId}`"
            :can-calculate-result="true"
            @delete-item="deleteItem"
            @calculate-result="(data: any) => meshEditor.calculateResult(data)"
          ></MeshTree>
        </el-scrollbar>
      </el-aside>
      <ToggleSidebar v-model="showTree" />
      <el-main v-loading="modelLoading" class="flex-grow-1 main-content" style="height: 93vh; overflow: hidden; position: relative;">
        <MeshEditor
          ref="meshEditor"
          v-model="meshTreeData"
          :fit-camera-to-scene="true"
          :activate-aline-to-floor="true"
          :has-toolbar="true"
          :fullscreen="false"
          :show-orientation-gizmo="true"
          :can-curve="activeOrderProgress === OrderProgress.crop || activeOrderProgress === OrderProgress.zip || activeOrderProgress === OrderProgress.spacer"
          :can-deform="activeOrderProgress === OrderProgress.adaptation"
          :can-transform="activeOrderProgress === OrderProgress.setup"
          :can-mirror="activeOrderProgress === OrderProgress.setup"
          :can-copy="activeOrderProgress === OrderProgress.setup"
          :can-aline="activeOrderProgress === OrderProgress.setup"
          :can-group="activeOrderProgress === OrderProgress.setup"
          :can-colorize="false"
          :can-embossing="activeOrderProgress === OrderProgress.embossing"
          :can-render="activeOrderProgress === OrderProgress.render"
          :allowed-curve-categories="allowedCurveCategories"
          :can-calculate-result="true"
        ></MeshEditor>
      </el-main>
    </el-container>
    <Workflow :workflow-model="workflowModel" :fullscreen-loading="fullscreenLoading" />
    <create-order v-if="!orderId" :template-id="templateId" />
  </el-container>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-facing-decorator';
import * as THREE from 'three';
import { useRouter } from 'vue-router';

import MeshTree from '@/components/three/scenes/MeshTree.vue';
import { MeshTreeData, MeshTreeDataItem } from '@/types/ui/MeshTreeData';
import OrientationGizmo from '@/components/three/OrientationGizmo.vue';
import MeshEditor from '@/components/three/scenes/MeshEditor.vue';
import { FileType3D, UploadCategory } from '@/types/enum/upload';
import { OrderProgress } from '@/types/enum/workflow';
import * as LayoutUtility from '@/utils/layout';
import * as modelService from '@/services/api/modelService';
import * as OrderService from '@/services/api/orderWorkflowService';
import ToggleSidebar from '@/components/ui-elements/ToggleSidebar.vue';
import Workflow from '@/components/workflow/Workflow.vue';
import { WorkflowModel } from '@/types/ui/WorkflowModel';
import { DefaultUndoRedo } from '@/types/ui/HistoryList';
import { CurveCategory } from '@/types/api/Model/Curve/CurveCategory';
import Auth from '@/services/api/auth';
import CreateOrder from '@/components/forms/CreateOrder.vue';
import { useFeatureFlagStore } from '@/stores/featureFlagStore';

@Component({
  components: {
    CreateOrder,
    MeshEditor,
    OrientationGizmo,
    MeshTree,
    ToggleSidebar,
    Workflow,
  },
  emits: ['update:orderId', 'back'], // Declare 'back' event so parent components can listen to it.
})
/* eslint-disable @typescript-eslint/no-explicit-any */
@Component
export default class OrderWorkflow extends Vue implements DefaultUndoRedo {
  @Prop() templateId!: number;
  @Prop({ default: null }) orderId!: number | null;
  router = useRouter();
  meshTreeData: MeshTreeData = new MeshTreeData(null, this);
  showTree = true;
  lastUpdateDB = -1;
  fullscreenLoading = false;
  modelLoading = false;
  meshEditor!: InstanceType<typeof MeshEditor>;

  workflowModel: WorkflowModel<OrderProgress> = new WorkflowModel<OrderProgress>('order-progress', OrderProgress);
  OrderProgress = OrderProgress;

  authentication = Auth.getInstance();
  featureFlagStore = useFeatureFlagStore();

  get allowedCurveCategories(): CurveCategory[] {
    switch (this.activeOrderProgress) {
      case OrderProgress.crop:
        return [CurveCategory.CROP];
      case OrderProgress.zip:
        return [CurveCategory.ZIP];
      case OrderProgress.spacer:
        return [CurveCategory.SPACER];
    }
    return Object.values(CurveCategory);
  }

  get activeOrderProgress(): OrderProgress {
    if (this.workflowModel.active < this.workflowModel.stepCount) {
      LayoutUtility.refresh();
      return Object.values(OrderProgress)[this.workflowModel.active];
    }
    return OrderProgress.setup;
  }

  set activeOrderProgress(value: OrderProgress) {
    this.workflowModel.setStep(Object.values(OrderProgress).indexOf(value));
  }

  unmounted(): void {
    if(!this.featureFlagStore.isOrderWorkflowEnabled()){
      this.router.back();
      return 
    }
    (this.router as any).askForChanges = false;
  }

  mounted(): void {
    window.onpopstate = () => location.reload();
    this.authentication.handleAuthentication();
    setTimeout(() => {
      if (this.$refs.meshEditor) {
        this.meshEditor = this.$refs.meshEditor as InstanceType<typeof MeshEditor>;
      }
    }, 100);
    this.loadFromDB();
  }

  loadFromDB(): void {
    if (this.orderId && !this.modelLoading) {
      this.modelLoading = true;
      this.meshTreeData.clear();
      OrderService.checkDataForId(this.orderId, this.templateId).then(async (importData) => {
        const orderId = this.orderId ? this.orderId : importData.orderId ? importData.orderId : 0;
        const isNew = !this.orderId;
        if (isNew && window.location.href.includes('order-workflow') && !window.location.href.includes('orderId')) {
          await this.router.replace(`/order-workflow?templateId=${this.templateId}&orderId=${orderId}`);
          this.$emit('update:orderId', orderId);
        }
        if (importData.meshes && importData.meshes.length > 0) {
          const loadChildren = (parent: string | MeshTreeDataItem | null, children: modelService.MeshExportData[]): void => {
            for (const data of children) {
              let treeData!: MeshTreeDataItem;
              if (data.fileType !== FileType3D.NONE) {
                treeData = this.meshTreeData.addBase64Import(
                  data.uniqueKey,
                  !this.orderId ? null : data.componentId,
                  data.name,
                  data.fileType,
                  data.base64,
                  data.thumbnailType,
                  data.thumbnail,
                  parent ? parent : (this as any).$t(`enum.upload-category.${data.category}`),
                  false,
                  data.color,
                  data.position,
                  data.rotation,
                  data.scale
                );
                treeData.bones = data.bones.map((boneInfo) => {
                  const bone = new THREE.Bone();
                  bone.name = boneInfo.name;
                  bone.position.copy(boneInfo.position);
                  return bone;
                });
              } else {
                treeData = this.meshTreeData.addGroup(
                  data.name,
                  [],
                  data.position,
                  data.rotation,
                  data.scale,
                  parent ? parent : (this as any).$t(`enum.upload-category.${data.category}`),
                  false
                );
                treeData.id = data.uniqueKey;
              }
              loadChildren(
                treeData,
                importData.meshes.filter((item) => item.parentId === data.uniqueKey)
              );
            }
          };

          loadChildren(
            null,
            importData.meshes.filter((item) => item.parentId === null)
          );

          setTimeout(() => {
            if (this.meshEditor) {
              for (const data of importData.meshes) {
                if (data.ffd) {
                  this.meshEditor.importFfdForMeshId(data.uniqueKey, data.ffd.spanCounts, data.ffd.gridPositionList);
                }
                if (data.embossing.length > 0) {
                  this.meshEditor.importEmbossingForMeshId(data.uniqueKey, data.embossing);
                }
              }
            }
          }, 500);

          OrderService.getCurveData(orderId).then((curveImport) => {
            setTimeout(() => {
              if (this.meshEditor) {
                this.meshEditor.importCurves(curveImport);
              }
            }, 500);
          });

          OrderService.getCameraData(orderId).then((cameraImport) => {
            setTimeout(() => {
              if (this.meshEditor) {
                this.meshEditor.importCameras(cameraImport);
              }
            }, 500);
          });

          if (isNew) {
            setTimeout(() => {
              this.saveUpdated();
            }, 500);
          }
          this.lastUpdateDB = Date.now();
        }
        this.modelLoading = false;
      });
    }
  }

  updateFromDB(): void {
    const getTreeData = (treeItemList: MeshTreeDataItem[], componentId: number | null): MeshTreeDataItem | undefined => {
      const result = treeItemList.find((item) => item.componentId === componentId);
      if (result) return result;
      for (const item of treeItemList) {
        const itemResult = getTreeData(item.children, componentId);
        if (itemResult) return itemResult;
      }
      return undefined;
    };

    if (this.orderId) {
      OrderService.getMeshData(this.orderId).then((importData) => {
        const selection = this.meshTreeData.getSelection().map((selectedItem) => selectedItem.id);
        this.meshTreeData.selectItem(null);
        if (this.orderId) {
          if (importData && importData.length > 0) {
            const loadChildren = (parent: string | null, children: modelService.MeshExportData[]): void => {
              for (const data of children) {
                let treeData: MeshTreeDataItem | undefined = undefined;
                if (data.fileType !== FileType3D.NONE) {
                  treeData = getTreeData(this.meshTreeData.treeData, data.componentId);
                  // Base64 assignment is not enough to update the geometry in the editor.
                  if (treeData && treeData.base64 !== data.base64) {
                    this.meshTreeData.removeItemTemp(treeData);
                    treeData = this.meshTreeData.addBase64Import(
                      data.uniqueKey,
                      data.componentId,
                      data.name,
                      data.fileType,
                      data.base64,
                      parent ? parent : (this as any).$t(`enum.upload-category.${data.category}`),
                      data.thumbnailType,
                      data.thumbnail,
                      false,
                      data.color,
                      data.position,
                      data.rotation,
                      data.scale
                    );
                  } else if (treeData) {
                    treeData.base64 = data.base64;
                    treeData.position = data.position;
                    treeData.rotation = data.rotation;
                    treeData.scale = data.scale;
                    treeData.color = data.color;
                    this.meshTreeData.setParent(treeData.uuid, parent ? parent : (this as any).$t(`enum.upload-category.${data.category}`));
                  }
                } else {
                  treeData = this.meshTreeData.addGroup(
                    data.name,
                    [],
                    data.position,
                    data.rotation,
                    data.scale,
                    parent ? parent : (this as any).$t(`enum.upload-category.${data.category}`),
                    false
                  );
                  treeData.id = data.uniqueKey;
                }
                if (treeData) {
                  loadChildren(
                    treeData.uuid,
                    importData.filter((item) => item.parentId === treeData?.uuid)
                  );
                }
              }
            };
            loadChildren(
              null,
              importData.filter((item) => item.parentId === null)
            );
            this.meshTreeData.updateStructure();
          }
        }
        if (selection.length > 0 && selection[0]) {
          const selectedItem = this.meshTreeData.getItemById(selection[0]);
          this.meshTreeData.selectItem(selectedItem);
        }
      });
    }
  }

  //#region Save Methods
  private updateTreeItem(categoryType: UploadCategory, item: MeshTreeDataItem, parent: MeshTreeDataItem, lastUpdateDB = -1): void {
    if (item.mesh && item.lastUpdateDB >= lastUpdateDB && this.orderId) {
      if (item.id) {
        OrderService.updateMeshData(
          this.orderId,
          this.templateId,
          item.id,
          categoryType,
          item.name,
          item.mesh,
          item.bones,
          parent.mesh ? parent.componentId : null,
          false
        ).then((id) => {
          item.id = id;
        });
      } else {
        OrderService.addMeshData(
          this.orderId,
          this.templateId,
          categoryType,
          item.name,
          item.mesh,
          item.bones,
          parent.mesh ? parent.componentId : null,
          null,
          false
        ).then((id) => {
          item.id = id;
        });
      }
      this.updateChildren(categoryType, item, lastUpdateDB);
    }
  }

  private updateChildren(categoryType: UploadCategory, parent: MeshTreeDataItem, lastUpdateDB = -1): void {
    for (const child of parent.children) {
      this.updateTreeItem(categoryType, child, parent, lastUpdateDB);
    }
  }

  private async saveAll(): Promise<void> {
    await this.save();
  }

  private async saveUpdated(): Promise<void> {
    await this.save(this.lastUpdateDB);
  }

  private async save(lastUpdateDB = -1): Promise<void> {
    this.fullscreenLoading = true;
    await setTimeout(() => {
      this.lastUpdateDB = Date.now();
      for (const category of this.meshTreeData.treeData) {
        const categoryType = Object.values(UploadCategory).find((categoryType) => category.name === (this as any).$t(`enum.upload-category.${categoryType}`));
        if (categoryType) {
          this.updateChildren(categoryType, category, lastUpdateDB);
        }
      }
      this.fullscreenLoading = false;
      (this.router as any).askForChanges = this.hasChanges;
    }, 100);
  }

  get hasChanges(): boolean {
    return this.lastUpdateDB < this.meshTreeData.lastUpdateDB;
  }

  @Watch('meshTreeData.lastUpdateDB', { immediate: false })
  async onMeshTreeDataDBChanged(): Promise<void> {
    (this.router as any).askForChanges = this.hasChanges;
  }
  //#endregion Save Methods

  deleteItem(item: MeshTreeDataItem): void {
    if (item.id && this.orderId) {
      OrderService.deleteMeshData(this.orderId, item.id);
    }
  }

  @Watch('showTree', { immediate: true })
  onShowTreeChanged(): void {
    LayoutUtility.refresh();
  }

  @Watch('workflowModel.active', { immediate: true })
  onActiveStepChanged(): void {
    if (this.workflowModel.navigationStart && this.hasChanges) {
      this.saveUpdated();
    }
  }

  @Watch('workflowModel.finished', { immediate: true })
  onStepFinished(): void {
    if (this.workflowModel.finished) {
      this.saveUpdated().then(() => {
        this.router.go(-1);
      });
    }
  }

  @Watch('orderId', { immediate: true })
  onOrderIdChanged(): void {
    this.meshTreeData.modelId = this.orderId;
    this.meshTreeData.historyList.modelId = this.orderId;
    this.meshTreeData.historyList.defaultUndoRedo = this;
    if (this.orderId && this.meshTreeData.treeData.length === 0 && !this.modelLoading) {
      this.loadFromDB();
    }
  }

  async defaultUndo(): Promise<void> {
    if (this.orderId) {
      await modelService.undo(this.orderId, 1).then(() => {
        // todo: geometry is updated in the backend with a delay. 500 milliseconds may not be enough.
        setTimeout(() => {
          this.updateFromDB();
        }, 500);
      });
    }
  }

  async defaultRedo(): Promise<void> {
    if (this.orderId) {
      await modelService.redo(this.orderId, 1).then(() => {
        // todo: geometry is updated in the backend with a delay. 500 milliseconds may not be enough.
        setTimeout(() => {
          this.updateFromDB();
        }, 500);
      });
    }
  }

  handleBack(): void {
    this.router.push('/projects');
  }
}
</script>

<style lang="scss" scoped>
.el-main :deep() {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
}

.level:not(:last-child) {
  margin-bottom: unset;
}

.screenHeight {
  display: flex;
  flex-direction: column;
  height: 93vh;
}

.minimize {
  width: 0;
}

.el-button.is-link {
  color: white;
}

.el-button.is-link:hover,
.el-button.is-link:focus {
  color: white;
}

.level:not(:last-child) {
  margin-bottom: unset;
}

.level,
.workflow,
.el-aside {
  z-index: 1;
}

.el-footer {
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep(.el-steps) {
  width: 100%;
}
</style>
